import { find, map } from "lodash-es";
import type { Key, MouseEvent } from "react";

import {
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSMenuContentItem,
  MDSMenuContentItemKind,
  MDSMenuItemSelectionVariant,
} from "@/mds";

import {
  StyledChipWrapper,
  StyledClearButton,
  StyledSelect,
  StyledTriggerContent,
  StyledTriggerLabel,
} from "./agent-filter-select.style";
import type { IAgentFilterSelectProps } from "./agent-filter-select.types";

export const AgentFilterSelect = ({
  label,
  selectedKey,
  defaultKey,
  items,
  leadingIcon,
  alwaysActive = false,
  onSelectionChange,
  onClear,
}: IAgentFilterSelectProps) => {
  const menuItems: MDSMenuContentItem[] = map(items, item => ({
    id: item.id,
    label: item.label,
    kind: MDSMenuContentItemKind.Item,
    icon: item.iconKind,
    isSelected: item.id === selectedKey,
    selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
  }));

  const selectedItem = find(items, item => item.id === selectedKey);
  const isActive = alwaysActive || selectedKey !== defaultKey;
  const showClearButton = isActive && !alwaysActive;
  const triggerIcon = leadingIcon ?? selectedItem?.iconKind;

  const handleSelectionChange = (key: Key | null) => {
    if (key === null) {
      return;
    }

    onSelectionChange({ key: String(key) });
  };

  const handleClearClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    onClear();
  };

  return (
    <StyledChipWrapper>
      <StyledSelect
        $isActive={isActive}
        $hasClearButton={showClearButton}
        label={label}
        selectedKey={selectedKey}
        items={menuItems}
        buttonVariant={MDSButtonVariant.Transparent}
        onSelectionChange={handleSelectionChange}
        popoverProps={{ style: { minWidth: 180, maxWidth: 280 } }}
        renderSelectValue={() => (
          <StyledTriggerContent>
            {triggerIcon ? <MDSIcon kind={triggerIcon} size={14} /> : null}
            <StyledTriggerLabel>{selectedItem?.label ?? label}</StyledTriggerLabel>
          </StyledTriggerContent>
        )}
      />
      {showClearButton ? (
        <StyledClearButton
          type="button"
          aria-label={`Clear ${label.toLowerCase()} filter`}
          onClick={handleClearClick}
        >
          <MDSIcon kind={MDSIconKind.Exit} size={10} />
        </StyledClearButton>
      ) : null}
    </StyledChipWrapper>
  );
};
